import { resolveProductFromPathname } from "../../i18n/routeLocale";
import { resolveRoutePath } from "../../i18n/routeGuards";
import { ROUTE_IDS } from "../../i18n/routeIds";
import {
  HOTEL_SEARCH_RESULTS_SNAPSHOT_SESSION_KEY,
  HOTEL_SEARCH_RESULTS_URL_SESSION_KEY,
  readHotelSearchResultsSnapshot,
  readHotelSearchResultsUrl,
  resolveHotelSearchEngineFallbackUrl,
  isHotelProfilePathname,
} from "./hotelSearchResultsNavigation";

export const HOTEL_SEARCH_CONTEXT_STORAGE_KEY = "currentSearchPhrase";

export const HOTEL_PROFILE_BACK_MISSING_CONTEXT_MESSAGE =
  "לא ניתן לחזור לתוצאות — פרטי החיפוש חסרים";

export const HOTEL_BACK_TO_RESULTS_SOURCE = Object.freeze({
  PREVIOUS_URL_LOCATION_STATE: "previousResultsUrl_locationState",
  PREVIOUS_URL_SESSION: "previousResultsUrl_sessionStorage",
  PREVIOUS_URL_SNAPSHOT: "previousResultsUrl_snapshot",
  SEARCH_CONTEXT_ROUTE: "searchContextRoute",
});

function pathnameFromUrl(url = "") {
  const raw = String(url).trim();
  if (!raw) return "";
  try {
    if (raw.startsWith("/")) {
      return raw.split("?")[0].split("#")[0];
    }
    return new URL(raw).pathname;
  } catch {
    return raw.split("?")[0].split("#")[0];
  }
}

export function isValidHotelSearchResultsReturnUrl(url) {
  if (!url || typeof url !== "string") return false;
  return !isHotelProfilePathname(pathnameFromUrl(url));
}

export function readPersistedHotelSearchContext() {
  try {
    const fromSession = sessionStorage.getItem(HOTEL_SEARCH_CONTEXT_STORAGE_KEY);
    if (fromSession) return JSON.parse(fromSession);
  } catch {
    /* ignore */
  }
  try {
    const fromLocal = localStorage.getItem(HOTEL_SEARCH_CONTEXT_STORAGE_KEY);
    if (fromLocal) return JSON.parse(fromLocal);
  } catch {
    /* ignore */
  }
  return null;
}

/**
 * Minimum phrase contract to rebuild hotel results navigation.
 */
export function hasHotelBackToResultsSearchContext(searchPhrase) {
  if (!searchPhrase || typeof searchPhrase !== "object") return false;
  if (searchPhrase._hotelsRiverColdUrlPlaceholder) return false;

  const checkIn = searchPhrase.checkIn ?? searchPhrase.checkin;
  const checkOut = searchPhrase.checkOut ?? searchPhrase.checkout;
  if (!checkIn || !checkOut) return false;

  const rooms = searchPhrase.rooms;
  if (!Array.isArray(rooms) || rooms.length === 0) return false;

  const hasDestination =
    (searchPhrase.lat != null && searchPhrase.lon != null) ||
    Boolean(searchPhrase.city) ||
    searchPhrase.hr_destination_id != null ||
    searchPhrase.destinationId != null;
  if (!hasDestination) return false;

  const currency = searchPhrase.currency;
  if (currency == null || String(currency).trim() === "") return false;

  const nationality =
    searchPhrase.countryCode ??
    searchPhrase.guestNationality ??
    searchPhrase.GuestNationality ??
    searchPhrase.nationality ??
    searchPhrase.residencyCountryCode ??
    null;
  if (nationality == null || String(nationality).trim() === "") return false;

  return true;
}

export function resolveHotelSearchResultsRoute(fromPathname, options = {}) {
  const { searchTab, isDynamicPackage = false } = options;
  const pathname =
    fromPathname ||
    (typeof window !== "undefined" ? window.location.pathname : "") ||
    "";
  const ctx = resolveProductFromPathname(pathname);
  const locale = ctx.locale || "he";

  const dynamicTab =
    isDynamicPackage ||
    searchTab === "DynamicPackages" ||
    String(searchTab || "").toLowerCase().includes("dynamic");

  if (dynamicTab) {
    const searchOffer = resolveRoutePath(ROUTE_IDS.SEARCH_OFFER, {
      locale,
      product: ctx.product,
      useLegacyAlias: ctx.isLegacyAlias !== false,
    });
    return searchOffer?.path || "/p/dashboard/searchoffer";
  }

  return resolveHotelSearchEngineFallbackUrl(pathname);
}

/**
 * Preserve results URL when navigating profile → profile (do not overwrite with profile path).
 */
export function resolveStoredHotelResultsUrlForProfileNavigation(location) {
  const candidates = [
    location?.state?.fromResultsUrl,
    readHotelSearchResultsUrl(),
    readHotelSearchResultsSnapshot()?.fromResultsUrl,
  ];

  for (const candidate of candidates) {
    if (isValidHotelSearchResultsReturnUrl(candidate)) {
      return candidate;
    }
  }

  const current = location?.pathname
    ? `${location.pathname}${location.search || ""}${location.hash || ""}`
    : null;
  if (isValidHotelSearchResultsReturnUrl(current)) {
    return current;
  }

  return null;
}

function resolvePreviousResultsUrl(locationState) {
  const fromLocationState = locationState?.fromResultsUrl;
  if (isValidHotelSearchResultsReturnUrl(fromLocationState)) {
    return {
      url: fromLocationState,
      sourceUsed: HOTEL_BACK_TO_RESULTS_SOURCE.PREVIOUS_URL_LOCATION_STATE,
    };
  }

  const sessionUrl = readHotelSearchResultsUrl();
  if (isValidHotelSearchResultsReturnUrl(sessionUrl)) {
    return {
      url: sessionUrl,
      sourceUsed: HOTEL_BACK_TO_RESULTS_SOURCE.PREVIOUS_URL_SESSION,
    };
  }

  const snapshotUrl = readHotelSearchResultsSnapshot()?.fromResultsUrl;
  if (isValidHotelSearchResultsReturnUrl(snapshotUrl)) {
    return {
      url: snapshotUrl,
      sourceUsed: HOTEL_BACK_TO_RESULTS_SOURCE.PREVIOUS_URL_SNAPSHOT,
    };
  }

  return { url: null, sourceUsed: null };
}

function buildNavigateState(snapshot, locationState) {
  return {
    restoreHotelSearchResults: true,
    restoreScrollY:
      locationState?.restoreScrollY ?? snapshot?.scrollPosition?.windowScrollY ?? 0,
    resultsContainerScrollTop:
      locationState?.resultsContainerScrollTop ??
      snapshot?.scrollPosition?.resultsContainerScrollTop ??
      0,
    filtersSnapshot:
      locationState?.filtersSnapshot ?? snapshot?.filtersState ?? null,
  };
}

function readSessionSearchContextRaw() {
  try {
    return sessionStorage.getItem(HOTEL_SEARCH_CONTEXT_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * SSOT — decide if/how hotel profile can navigate back to search results.
 */
export function resolveHotelBackToResultsNavigation({
  currentHotelId = null,
  currentUrl = null,
  locationState = null,
  fromPathname = null,
  searchPhrase = null,
  searchTab = null,
  isDynamicPackage = false,
} = {}) {
  const snapshot = readHotelSearchResultsSnapshot();
  const persistedPhrase = searchPhrase ?? readPersistedHotelSearchContext();
  const searchContextExists = hasHotelBackToResultsSearchContext(persistedPhrase);
  const searchResultsRoute = resolveHotelSearchResultsRoute(fromPathname, {
    searchTab,
    isDynamicPackage,
  });

  const previous = resolvePreviousResultsUrl(locationState);
  const previousResultsUrl = previous.url;

  const baseTrace = {
    currentHotelId: currentHotelId != null ? String(currentHotelId) : null,
    currentUrl,
    searchContextExists,
    searchResultsRoute,
    previousResultsUrl,
    locationState: locationState ?? null,
    sessionStorageSearchContextKey: HOTEL_SEARCH_CONTEXT_STORAGE_KEY,
    sessionStorageSearchContextPresent: Boolean(readSessionSearchContextRaw()),
    sessionStorageResultsUrlKey: HOTEL_SEARCH_RESULTS_URL_SESSION_KEY,
    sessionStorageResultsSnapshotKey: HOTEL_SEARCH_RESULTS_SNAPSHOT_SESSION_KEY,
  };

  if (previousResultsUrl) {
    return {
      canNavigate: true,
      targetUrl: previousResultsUrl,
      reason: null,
      sourceUsed: previous.sourceUsed,
      navigateState: buildNavigateState(snapshot, locationState),
      trace: baseTrace,
    };
  }

  if (searchContextExists) {
    return {
      canNavigate: true,
      targetUrl: searchResultsRoute,
      reason: null,
      sourceUsed: HOTEL_BACK_TO_RESULTS_SOURCE.SEARCH_CONTEXT_ROUTE,
      navigateState: buildNavigateState(snapshot, locationState),
      trace: baseTrace,
    };
  }

  // Dynamic package profile: never hard-block "back" — fall back to search engine route
  // even when phrase/snapshot is incomplete (refresh / lean / cold URL).
  if (isDynamicPackage && searchResultsRoute) {
    return {
      canNavigate: true,
      targetUrl: searchResultsRoute,
      reason: "dynamic_package_soft_fallback_missing_search_context",
      sourceUsed: HOTEL_BACK_TO_RESULTS_SOURCE.SEARCH_CONTEXT_ROUTE,
      navigateState: buildNavigateState(snapshot, locationState),
      trace: {
        ...baseTrace,
        softFallback: true,
      },
    };
  }

  return {
    canNavigate: false,
    targetUrl: null,
    reason: "missing_search_context",
    sourceUsed: null,
    navigateState: null,
    trace: baseTrace,
  };
}

export function traceHotelProfileBackToResults(payload) {
  try {
    // eslint-disable-next-line no-console
    console.info("[HOTEL_PROFILE_BACK_TO_RESULTS_TRACE]", {
      ts: Date.now(),
      ...payload,
    });
  } catch {
    /* ignore */
  }
}
